import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Sheet,
    Typography,
    Box,
    Button,
    Table,
    IconButton,
    FormControl,
    FormLabel,
    Input,
    Select,
    Option,
    Modal,
    ModalDialog,
    ModalClose,
    Divider,
    Stack,
    Alert,
    CircularProgress,
    Breadcrumbs,
    Link
} from '@mui/joy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import domain from '../utils/domain';

interface Section {
    id: number;
    name: string;
    marks_per_question: number;
}

interface ExamSection {
    id: number;
    exam_id: number;
    section_id: number;
    section_name: string;
    section_order: number;
    easy_questions: number;
    medium_questions: number;
    hard_questions: number;
    total_questions: number;
    marks_per_question: number;
}

interface Exam {
    id: number;
    title: string;
}

const ManageExamSections: React.FC = () => {
    const navigate = useNavigate();
    const { examId } = useParams<{ examId: string }>();
    const [exam, setExam] = useState<Exam | null>(null);
    const [sections, setSections] = useState<Section[]>([]);
    const [examSections, setExamSections] = useState<ExamSection[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSection, setSelectedSection] = useState<number | null>(null);
    const [questionCounts, setQuestionCounts] = useState({
        easy: 0,
        medium: 0,
        hard: 0
    });

    useEffect(() => {
        fetchData();
    }, [examId]);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            // Fetch exam details
            const examResponse = await fetch(`${domain}/api/admin/exams/${examId}`, {
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include'
            });

            if (!examResponse.ok) {
                throw new Error('Failed to fetch exam details');
            }

            const examData = await examResponse.json();
            setExam(examData.exam);

            // Fetch available sections
            const sectionsResponse = await fetch(`${domain}/api/admin/sections`, {
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include'
            });

            if (!sectionsResponse.ok) {
                throw new Error('Failed to fetch sections');
            }

            const sectionsData = await sectionsResponse.json();
            setSections(sectionsData.sections);

            // Fetch exam sections
            const examSectionsResponse = await fetch(`${domain}/api/admin/exams/${examId}/sections`, {
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include'
            });

            if (!examSectionsResponse.ok) {
                throw new Error('Failed to fetch exam sections');
            }

            const examSectionsData = await examSectionsResponse.json();
            setExamSections(examSectionsData.examSections);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load data');
            setTimeout(() => {
                setError('');
            }, 5000);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddSection = async () => {
        if (!selectedSection) return;

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/exams/${examId}/sections`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({
                    section_id: selectedSection,
                    easy_questions: questionCounts.easy,
                    medium_questions: questionCounts.medium,
                    hard_questions: questionCounts.hard
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(`Failed to add section: ${errorData.message}`);
            }

            await fetchData();
            setIsModalOpen(false);
            setSelectedSection(null);
            setQuestionCounts({ easy: 0, medium: 0, hard: 0 });
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add section');
            setTimeout(() => {
                setError('');
            }, 5000);
            setIsModalOpen(false)
        }
    };

    const handleRemoveSection = async (examSectionId: number) => {
        if (!window.confirm('Are you sure you want to remove this section?')) {
            return;
        }

        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/exams/${examId}/sections/${examSectionId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
                credentials: 'include'
            });

            if (!response.ok) {
                throw new Error('Failed to remove section');
            }

            await fetchData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to remove section');
            setTimeout(() => {
                setError('');
            }, 5000);
        }
    };

    const handleMoveSection = async (examSectionId: number, direction: 'up' | 'down') => {
        try {
            const token = localStorage.getItem('adminToken');
            if (!token) {
                navigate('/admin');
                return;
            }

            const response = await fetch(`${domain}/api/admin/exams/${examId}/sections/${examSectionId}/move`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify({ direction })
            });

            if (!response.ok) {
                throw new Error('Failed to move section');
            }

            await fetchData();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to move section');
            setTimeout(() => {
                setError('');
            }, 5000);
        }
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: 'background.level1' }}>
            <Sheet
                variant="plain"
                sx={{
                    p: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'white',
                    borderBottom: '2px solid',
                    borderColor: '#ea7a23',
                    color: '#ea7a23'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <img
                        src={`${process.env.PUBLIC_URL}/logo-vector.svg`}
                        alt="Vector India Logo"
                        style={{ width: '200px', height: 'auto' }}
                    />
                    <Typography level="h4" sx={{ color: '#ea7a23' }}>
                        Manage Exam Sections
                    </Typography>
                </Box>
            </Sheet>

            <Box sx={{ p: 2, maxWidth: '1200px', margin: '0 auto' }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <Link href="/admin/dashboard">Dashboard</Link>
                    <Link href="/admin/dashboard">Exams</Link>
                    <Typography>Manage Sections - {exam?.title}</Typography>
                </Breadcrumbs>

                {error && (
                    <Alert color="danger" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography level="h4">Exam Sections</Typography>
                    <Button
                        startDecorator={<AddIcon />}
                        onClick={() => setIsModalOpen(true)}
                    >
                        Add Section
                    </Button>
                </Box>

                <Sheet variant="outlined">
                    <Table>
                        <thead>
                            <tr>
                                <th>Order</th>
                                <th>Section Name</th>
                                <th>Easy Questions</th>
                                <th>Medium Questions</th>
                                <th>Hard Questions</th>
                                <th>Total Questions</th>
                                <th>Marks/Question</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {examSections.map((examSection, index) => (
                                <tr key={examSection.id}>
                                    <td>{examSection.section_order}</td>
                                    <td>{examSection.section_name}</td>
                                    <td>{examSection.easy_questions}</td>
                                    <td>{examSection.medium_questions}</td>
                                    <td>{examSection.hard_questions}</td>
                                    <td>{examSection.total_questions}</td>
                                    <td>{examSection.marks_per_question}</td>
                                    <td>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            {index > 0 && (
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => handleMoveSection(examSection.id, 'up')}
                                                >
                                                    <ArrowUpwardIcon />
                                                </IconButton>
                                            )}
                                            {index < examSections.length - 1 && (
                                                <IconButton
                                                    size="sm"
                                                    variant="plain"
                                                    color="neutral"
                                                    onClick={() => handleMoveSection(examSection.id, 'down')}
                                                >
                                                    <ArrowDownwardIcon />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="sm"
                                                variant="plain"
                                                color="danger"
                                                onClick={() => handleRemoveSection(examSection.id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    </td>
                                </tr>
                            ))}
                            <tr style={{ fontWeight: 'bold', backgroundColor: 'var(--joy-palette-background-level1)' }}>
                                <td colSpan={2} style={{ textAlign: 'right' }}>Totals:</td>
                                <td>{examSections.reduce((sum, section) => sum + section.easy_questions, 0)}</td>
                                <td>{examSections.reduce((sum, section) => sum + section.medium_questions, 0)}</td>
                                <td>{examSections.reduce((sum, section) => sum + section.hard_questions, 0)}</td>
                                <td>{examSections.reduce((sum, section) => sum + section.total_questions, 0)}</td>
                                <td colSpan={2} style={{ textAlign: 'right' }}>
                                    Total Marks: {examSections.reduce((sum, section) =>
                                        sum + (section.total_questions * section.marks_per_question), 0
                                    )}
                                </td>
                                <td></td>
                            </tr>
                        </tbody>
                    </Table>
                </Sheet>

                <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                    <ModalDialog>
                        <ModalClose />
                        <Typography level="h4">Add Section to Exam</Typography>
                        <Divider sx={{ my: 2 }} />
                        <form onSubmit={(e) => { e.preventDefault(); handleAddSection(); }}>
                            <Stack spacing={2}>
                                <FormControl required>
                                    <FormLabel>Select Section</FormLabel>
                                    <Select
                                        value={selectedSection?.toString() || ''}
                                        onChange={(_, value) => setSelectedSection(value ? parseInt(value) : null)}
                                    >
                                        {sections.map((section) => (
                                            <Option key={section.id} value={section.id.toString()}>
                                                {section.name}
                                            </Option>
                                        ))}
                                    </Select>
                                </FormControl>

                                <FormControl required>
                                    <FormLabel>Easy Questions</FormLabel>
                                    <Input
                                        type="number"
                                        value={questionCounts.easy}
                                        onChange={(e) => setQuestionCounts(prev => ({
                                            ...prev,
                                            easy: parseInt(e.target.value) || 0
                                        }))}
                                        slotProps={{
                                            input: {
                                                min: 0
                                            }
                                        }}
                                    />
                                </FormControl>

                                <FormControl required>
                                    <FormLabel>Medium Questions</FormLabel>
                                    <Input
                                        type="number"
                                        value={questionCounts.medium}
                                        onChange={(e) => setQuestionCounts(prev => ({
                                            ...prev,
                                            medium: parseInt(e.target.value) || 0
                                        }))}
                                        slotProps={{
                                            input: {
                                                min: 0
                                            }
                                        }}
                                    />
                                </FormControl>

                                <FormControl required>
                                    <FormLabel>Hard Questions</FormLabel>
                                    <Input
                                        type="number"
                                        value={questionCounts.hard}
                                        onChange={(e) => setQuestionCounts(prev => ({
                                            ...prev,
                                            hard: parseInt(e.target.value) || 0
                                        }))}
                                        slotProps={{
                                            input: {
                                                min: 0
                                            }
                                        }}
                                    />
                                </FormControl>

                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="plain"
                                        color="neutral"
                                        onClick={() => setIsModalOpen(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit">
                                        Add Section
                                    </Button>
                                </Box>
                            </Stack>
                        </form>
                    </ModalDialog>
                </Modal>
            </Box>
        </Box>
    );
};

export default ManageExamSections; 